import { useMemo, useState } from 'react';

import {
  ChakraFlex,
  ChakraVStack,
  ContactInfoCardProps,
  InquiryCoverageCardProps,
  NameWithAvatarCardProps,
  NoteMaudCardV2Props,
  ProviderAvatarBlockCardProps,
} from '@equip.health/ui';

import EditAdmissionsTeamModal from '~/components/maud/inquiries/InquiryDetail/EditAdmissionsTeamModal';
import {
  AdmissionTeam,
  Appointments,
  Contacts,
  Coverages,
  InquiryInfo,
  Notes,
  PatientChart,
  Tracking,
} from '~/components/maud/inquiries/InquiryDetail/sections';
import { NextSteps } from '~/components/maud/inquiries/InquiryDetail/sections/NextSteps';
import { ReviewsSection } from '~/components/maud/inquiries/InquiryDetail/sections/Reviews';
import { TaskCardProps, Tasks } from '~/components/common/Tasks';
import { InquiryDetailState } from '~/components/maud/inquiries/InquiryDetail/useInquiryDetail';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  fromInquiry,
  FromInquiry,
  FromNotes,
  fromNotes,
  getAdmissionTeamPropsOnProviderData,
  getAppointmentSectionProps,
  getContactSectionPropFromPatient,
  getContactSectionProps,
  getCoverageSectionProps,
  getIsPatientPrimaryContact,
  getPatientSectionProps,
  getTaskProps,
} from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { AppointmentSectionData } from '~/lib/util/appointment.util';
import { toLocalMidnight } from '~/lib/util/date.util';
import { useUpdateInquiryAssignmentsMutation } from '~/store/services/inquiry';
import ReviewDuplicateDataButton from './sections/ReviewDuplicateDataButton';
import CareTeam from './sections/CareTeam';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { TaskEvents } from '~/analytics/taskEvents';

export interface ViewInquiryEvents {
  onAddNote: () => void;
  onAssignCareTeamProvider: (providerType?: CareTeamProviderType) => void;
  onAddTask: () => void;
  onChangeTaskStatus: (
    taskId: string,
    status: string | null,
    voidReasonExternalId?: string,
  ) => void;
  onCreatePatientChart: () => void;
  onConfirmPatient: (patientId: string) => void;
  onEditNote: (note: NoteMaudCardV2Props) => void;
  onFetchDuplicateContacts: () => Promise<void>;
  onFetchDuplicateCoverages: () => Promise<void>;
  onLoadMoreNotes: () => void;
  onPatientClick: () => void;
  onVoidNote: (note: NoteMaudCardV2Props) => void;
}

export interface ViewInquiryProps {
  eventHandlers: ViewInquiryEvents;
  id: string;
  inquiryDetailState: InquiryDetailState;
}

export const ViewInquiry = ({
  eventHandlers,
  id,
  inquiryDetailState,
}: ViewInquiryProps) => {
  const { trackEvent } = useAnalytics();
  const {
    appointments,
    assignedCareTeamProviders,
    contacts,
    coverages,
    fetchAppointmentsLoading,
    fetchContactsLoading,
    fetchCoveragesLoading,
    fetchDuplicateContactsLoading,
    fetchDuplicateCoveragesLoading,
    fetchInquiryLoading,
    fetchNotesLoading,
    fetchPatientLoading,
    fetchPatientMatchesLoading,
    fetchTasksLoading,
    inquiry,
    fetchProvidersLoading,
    isPatientMatchConfirmed,
    notes,
    patient,
    patientMatches,
    providers,
    tasks,
  } = inquiryDetailState;

  const TOPLEVEL_ID = `${id}__inquiry-detail`;
  const {
    enableAdmittingTreatmentProgram: isAdmittingTreatmentProgramEnabled,
    providerAssignmentMaud: isProviderAssignmentMaudEnabled,
  } = useFlags<LDFlags>();

  const showCareTeamSection = useMemo<boolean>(() => {
    return (
      isProviderAssignmentMaudEnabled &&
      [
        InquiryStatusValue.ONBOARDING.toString(),
        InquiryStatusValue.ENROLLED.toString(),
      ].includes(inquiry?.status?.name)
    );
  }, [inquiry?.status?.name, isProviderAssignmentMaudEnabled]);

  const [updateAssignments, { isLoading: isUpdatingAssignments }] =
    useUpdateInquiryAssignmentsMutation();

  const providersRecord = useMemo<
    Record<string, GetProviderByIdResponse>
  >(() => {
    const providersRecord: Record<string, GetProviderByIdResponse> = {};
    if (providers?.data) {
      providers.data.forEach((p) => {
        providersRecord[p.userId] = p;
      });
    }
    return providersRecord;
  }, [providers]);

  const { nextContactDate, infoProps } = useMemo<FromInquiry>(() => {
    const result: FromInquiry = fromInquiry(inquiry, patient);
    if (!isAdmittingTreatmentProgramEnabled) {
      result.infoProps.targetTreatmentProgram = null;
    }
    return result;
  }, [patient, inquiry, isAdmittingTreatmentProgramEnabled]);

  const admissionsTeam = useMemo<ProviderAvatarBlockCardProps[]>(() => {
    if (inquiry && providersRecord) {
      return getAdmissionTeamPropsOnProviderData(inquiry, providersRecord);
    }
    return [];
  }, [inquiry, providersRecord]);

  const { count, noteProps, previousContactDate } = useMemo<FromNotes>(() => {
    return fromNotes(notes, providersRecord, inquiry?.patient?.id);
  }, [notes, providersRecord, inquiry?.patient?.id]);

  const coverageProps = useMemo<InquiryCoverageCardProps[]>(() => {
    return getCoverageSectionProps(inquiry, coverages ?? []);
  }, [inquiry, coverages]);

  const appointmentProps = useMemo<AppointmentSectionData>(() => {
    return getAppointmentSectionProps(appointments);
  }, [appointments]);

  const patientProps = useMemo<NameWithAvatarCardProps[]>(() => {
    return patient ? getPatientSectionProps([patient]) : [];
  }, [patient]);

  const contactProps = useMemo<ContactInfoCardProps[]>(() => {
    if (patient) {
      const patientContactProps = getContactSectionPropFromPatient(
        patient,
        contacts,
      );
      if (patientContactProps) {
        return [patientContactProps, ...getContactSectionProps(contacts)];
      }
    }
    return getContactSectionProps(contacts);
  }, [contacts, patient]);

  const taskProps = useMemo<TaskCardProps[]>(() => {
    return getTaskProps(tasks);
  }, [tasks]);

  const [isEditAdmissionsTeamModalOpen, setIsEditAdmissionsTeamModalOpen] =
    useState<boolean>(false);

  const [admissionsTeamMembers, setAdmissionsTeamMembers] = useState<
    ProviderAvatarBlockForState[]
  >([]);

  const { showErrorToast } = useErrorToast();
  const onEditAdmissionsTeamModalOpen = () => {
    setIsEditAdmissionsTeamModalOpen(true);
  };
  const onEditAdmissionsTeamModalCancel = () => {
    setIsEditAdmissionsTeamModalOpen(false);
  };

  const onEditAdmissionsTeamModalConfirm = () => {
    updateAssignments({
      assignments: admissionsTeamMembers.map((atm) => ({
        externalId: atm.id,
        firstName: atm.firstName,
        lastName: atm.lastName,
        providerType: atm.providerTypeName,
      })),
      inquiryId: inquiry.id,
      version: inquiry.version,
    })
      .unwrap()
      .then(() => {
        onEditAdmissionsTeamModalCancel();
      })
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.UPDATE_INQUIRY_ASSIGNMENTS,
          error.status,
          error.data?.message,
        );
      });

    setIsEditAdmissionsTeamModalOpen(false);
  };

  const isPatientPrimaryContact = getIsPatientPrimaryContact(
    patient?.userId,
    inquiry?.primaryContact?.id,
  );

  const handleAddTask = () => {
    trackEvent(TaskEvents.addNewTaskButtonClickedFromInquiryDetailPanel, {
      source: AnalyticsSourceValues.INQUIRY_DETAILS,
      taskId: inquiry?.id,
      patientId: inquiry?.patient?.id,
    });
    eventHandlers.onAddTask();
  };

  return (
    <ChakraVStack
      alignItems="stretch"
      gap="24px"
      paddingTop="24px"
      width="100%"
    >
      <NextSteps
        id={TOPLEVEL_ID}
        inquiryId={inquiry?.id}
        isLoading={fetchInquiryLoading}
        value={inquiry?.nextSteps}
        version={inquiry?.version}
      />
      <ChakraFlex gap="40px" width="100%">
        <ChakraVStack alignItems="start" gap="36px" width="100%">
          {inquiry?.isTrackingEligible && (
            <ReviewsSection
              id={TOPLEVEL_ID}
              inquiryId={inquiry?.id}
              patientId={inquiry?.patient?.id}
              shouldInvalidate
              version={inquiry?.version}
            />
          )}
          <Notes
            count={count}
            id={TOPLEVEL_ID}
            isLoading={
              fetchNotesLoading || fetchInquiryLoading || fetchProvidersLoading
            }
            notes={noteProps}
            onAdd={eventHandlers.onAddNote}
            onEdit={eventHandlers.onEditNote}
            onLoadMoreNotes={eventHandlers.onLoadMoreNotes}
            onVoid={eventHandlers.onVoidNote}
          />
          <Tasks
            id={TOPLEVEL_ID}
            isLoading={fetchTasksLoading || fetchInquiryLoading}
            onAdd={handleAddTask}
            onChangeStatus={eventHandlers.onChangeTaskStatus}
            tasks={taskProps}
            patientId={inquiry?.patient?.id}
          />
          <Coverages
            coverages={coverageProps}
            id={TOPLEVEL_ID}
            isLoading={fetchCoveragesLoading}
          >
            {isPatientMatchConfirmed && (
              <ReviewDuplicateDataButton
                id={`${TOPLEVEL_ID}__coverages`}
                isLoading={fetchDuplicateCoveragesLoading}
                onClick={eventHandlers.onFetchDuplicateCoverages}
              />
            )}
          </Coverages>
          <Appointments
            id={TOPLEVEL_ID}
            isLoading={fetchAppointmentsLoading || fetchInquiryLoading}
            {...appointmentProps}
            inquiryStatus={inquiry?.status?.name as InquiryStatusValue}
            inquiryExternalId={inquiry?.id}
            patient={patient}
          />
        </ChakraVStack>
        <ChakraVStack gap="36px" maxWidth="350px" minWidth="350px">
          <Tracking
            id={TOPLEVEL_ID}
            inquiryId={inquiry?.id}
            isLoading={fetchInquiryLoading || fetchNotesLoading}
            isPatientPrimaryContact={isPatientPrimaryContact}
            isTrackingEligible={inquiry?.isTrackingEligible}
            nextDate={nextContactDate}
            patientId={inquiry?.patient?.id}
            previousDate={previousContactDate}
            targetAdmissionDate={
              inquiry?.targetAdmissionDate
                ? toLocalMidnight(new Date(inquiry.targetAdmissionDate))
                : null
            }
            version={inquiry?.version}
          />
          <PatientChart
            id={TOPLEVEL_ID}
            isLoading={
              fetchPatientLoading ||
              fetchInquiryLoading ||
              fetchPatientMatchesLoading
            }
            onConfirm={eventHandlers.onConfirmPatient}
            onCreate={eventHandlers.onCreatePatientChart}
            onPatientClick={eventHandlers.onPatientClick}
            patientMatchConfirmedAt={inquiry?.patientMatchConfirmedAt}
            patientMatches={patientMatches}
            patients={patientProps}
          />
          <Contacts
            contacts={contactProps}
            id={TOPLEVEL_ID}
            isLoading={fetchContactsLoading}
          >
            {isPatientMatchConfirmed && (
              <ReviewDuplicateDataButton
                id={`${TOPLEVEL_ID}__contacts`}
                isLoading={fetchDuplicateContactsLoading}
                onClick={eventHandlers.onFetchDuplicateContacts}
              />
            )}
          </Contacts>
          <AdmissionTeam
            id={TOPLEVEL_ID}
            isLoading={fetchInquiryLoading}
            members={admissionsTeam}
            onEdit={onEditAdmissionsTeamModalOpen}
          />
          {showCareTeamSection && (
            <CareTeam
              isLoading={fetchProvidersLoading}
              onAssign={eventHandlers.onAssignCareTeamProvider}
              providers={assignedCareTeamProviders}
            />
          )}
          <InquiryInfo
            id={TOPLEVEL_ID}
            info={infoProps}
            isLoading={fetchInquiryLoading || fetchPatientLoading}
          />
        </ChakraVStack>
        {isEditAdmissionsTeamModalOpen && ( // modal needs to be unmounted when closed to reset search and unsaved changes
          <EditAdmissionsTeamModal
            admissionsTeamMembers={admissionsTeamMembers}
            initialAdmissionsTeamMembers={admissionsTeam}
            isOpen={isEditAdmissionsTeamModalOpen}
            isUpdatingAssignments={isUpdatingAssignments}
            onCancel={onEditAdmissionsTeamModalCancel}
            onConfirm={onEditAdmissionsTeamModalConfirm}
            setAdmissionsTeamMembers={setAdmissionsTeamMembers}
          />
        )}
      </ChakraFlex>
    </ChakraVStack>
  );
};
